import '../../Common/vendor/element.prototype.polyfills.js';

import {
	find,
	remove,
	map,
	uniqueId,
	isFunction,
	forEach,
	reduce,
	mergeWith,
	isArray,
	isObject,
	has,
	invoke,
	get,
	includes,
	reverse,
} from 'lodash';


import { commonConstants } from '../constants/common.constants';
import { ICommonResponseError } from '../interfaces/response';
import { CommonBaseService } from '../common-base-service.class/common-base-service.class';
import {
	Injectable,
	Injector,
	SecurityContext
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { PROMOTION_BANNER_EVENT } from '@CaseOne/Common/promotion-banner/promotion-banner-event.enum';
import { CommonEventsService } from '@CaseOne/Common/events/services/events.service';
import { UUID } from '@CaseOne/Common/interfaces/core';
import { appendStyle } from '@CaseOne/Common/utilities/html';
import Dotdotdot from '../libs/dotdotdot/dotdotdot';
import { DomSanitizer } from '@angular/platform-browser';

export interface INoty {
	id: string
	shown: boolean,
	queued: boolean,
	timer: number,
	isClosing: boolean,
	$element: JQuery<HTMLElement>,
	$body: JQuery<HTMLElement>,
	$buttons: JQuery<HTMLElement>,
	show: () => void,
	close: () => void,
	truncate: () => void,
}

export interface ICommonNotyOptionsAnimation {
	open: string,
	close: string,
}

export interface ICommonNotyOptionsButton {
	callback?: (noty: INoty) => void,
	className?: string,
	title: string,
	href?: string,
	openInCurrentWindow?: boolean,
	closeAfterClick?: boolean,
}

export interface ICommonNotyOptions {
	animation?: ICommonNotyOptionsAnimation,
	buttons?: ICommonNotyOptionsButton[],
	icon?: string,
	layout?: string,
	text?: string,
	timeout?: number,
	type?: string,
	notificationId?: string,
}

export type TCommonNotificationType = 'success' | 'info'| 'error' | 'delete_success' | 'promotion' | string;
export type TCommonNotificationIconType = 'alert';

export interface ICommonNotyParams {
	buttons?: ICommonNotyOptionsButton[],
	iconType?: TCommonNotificationIconType,
	notificationId?: string,
}

declare const require: any;

@Injectable({
	providedIn: 'root',
})
export class CommonNotificationService extends CommonBaseService {
	private	NOTIFICATION_HEIGHT = 208;
	protected NOTIFICATION_TIMEOUT = 10000;

	private commonEventsService = this.injector.get(CommonEventsService);
	private domSanitizer = this.injector.get(DomSanitizer);

	protected notificationsQueue = {
		_queue: [],
		add: (noty: INoty) => {
			if (!find<INoty>(this.notificationsQueue._queue, noty)) {
				this.notificationsQueue._queue.push(noty);
				noty.queued = true;
			}
			return this.notificationsQueue;
		},
		remove: (noty: INoty) => {
			noty.queued = false;
			remove<INoty>(this.notificationsQueue._queue, noty);
			return this.notificationsQueue;
		},
		next: () => {
			return this.notificationsQueue._queue[0];
		},
	};
	public shownCache: Array<{
		noty: INoty,
		height: number,
	}> = [];

	private animationStartEvents = 'webkitAnimationStart animationstart';
	private $body = $('body');
	private window = this.injector.get(DOCUMENT).defaultView;
	private loadStylesPromise: Promise<any>;

	init () {
		if (!this.loadStylesPromise) {
			this.loadStylesPromise = require.ensure(
				['./styles/notification.sass'],
				// tslint:disable-next-line:no-shadowed-variable
				(require) => require('./styles/notification.sass'),
				(error) => { throw error; },
				'notification-styles',
			).then((module) => {
				appendStyle(module.default);
			});
		}

		return this.loadStylesPromise;
	}

	show (
		title: string,
		body: string | {},
		type: TCommonNotificationType,
		params?: ICommonNotyParams,
	): void {
		const notificationId = params?.notificationId || uniqueId('common_noty_bar_');
		const shownItem = find(this.shownCache, (item) => {
			return item.noty.id === notificationId && !item.noty.isClosing;
		});

		if (shownItem) {
			this.restartClosingTimer(shownItem.noty);
		} else {
			const	innerLayout = this.getMessage(title, body);

			// Without zonejs
			setTimeout(() => {
				this.createNotification({
					text: innerLayout,
					type: invoke(type, 'toLowerCase'),
					icon: params?.iconType ? 'icon_' + params.iconType : '',
					buttons: params?.buttons,
					notificationId,
				});
			}, 0);
		}
	}

	handleResponseError (response: ICommonResponseError): void {
		const errorHeader = (get(response, 'data.ErrorHeader') || get(response, 'error.ErrorHeader')) || this.commonLocaleService.instant('common.noty.error.title');
		const errorTypes = ['General', 'Subscription', 'ConditionFailed'];
		const exceptionDetailUrl = get(response, 'data.ExceptionDetailUrl') || get(response, 'error.ExceptionDetailUrl') || response.ExceptionDetailUrl;
		const notificationParams: ICommonNotyOptions = {};
		let errorText = get(response, 'data.Error') || get(response, 'error.Error') || response.Error || '';
		let errorType = get(response, 'data.ErrorType') || get(response, 'error.ErrorType');

		if (exceptionDetailUrl) {
			errorText += `\n${this.commonLocaleService.instant('common.noty.error.download_log_text')}`;
			notificationParams.buttons = [
				{
					title: this.commonLocaleService.instant('common.noty.error.download_log_btn_title'),
					callback: () => {
						this.window.open(exceptionDetailUrl, '_blank');
					},
				},
			];
		}

		if (!includes(errorTypes, errorType)) {
			errorType = 'error';
		}

		errorType = (errorType || '').toLowerCase();
		this.show(errorHeader, errorText, errorType, notificationParams);
	}

	protected createNotificationLayout (noty: INoty, options: ICommonNotyOptions): JQuery<HTMLElement> {
		const baseClass: string = options.type !== 'promotion' ? 'b-noty_bar' : 'b-noty_bar_promotion';
		noty.$buttons.append(this.createNotificationButtons(noty, options.buttons, options.type));
		noty.$body.append(options.text);

		return noty.$element
			.addClass([
				baseClass,
				`b-noty_type__${options.type}`,
				`b-noty_theme__${options.icon}`,
			].join(' '))
			.append([noty.$body, noty.$buttons]);
	}

	private createNotificationButtons (noty: INoty, buttons: ICommonNotyOptionsButton[], type?: TCommonNotificationType): Array<JQuery<HTMLElement>>  {
		return map(buttons, (button) => {
			let commonAttrs;
			let $button: JQuery<HTMLElement>;

			button.className = button.className || '';
			button.title = button.title || '';
			commonAttrs = `id='${uniqueId('common_noty_button_')}' class='${button.className}'`;

			if (button.href) {
				$button = $(`<a href='${button.href}' target='${!button.openInCurrentWindow ? '_blank' : '_self'}' ${commonAttrs}>${button.title}</a>`);
			} else {
				$button = $(`<button type='button' ${commonAttrs}>${button.title}</button>`);
			}

			$button.on('click', () => {
				if (type === 'promotion') {
					this.initPromotionalEvent(noty, button);
				}
			});

			if (isFunction(button.callback)) {
				// Click with zonejs
				$button.on('click', () => {
					button.callback(noty);
				});
			}

			if (button.closeAfterClick) {
				// Click with zonejs
				$button.on('click', () => {
					noty.close();
				});
			}

			return $button;
		});
	}

	private getMaxVisible (): number {
		return Math.floor(window.innerHeight / this.NOTIFICATION_HEIGHT);
	}

	private getContainer (options: ICommonNotyOptions): JQuery<HTMLElement> {
		let $notificationsContainer = this.$body.find('#common_noty_layout__' + options.layout);

		if ($notificationsContainer.length === 0) {
			$notificationsContainer = $(`<div id='common_noty_layout__${options.layout}'></div>`);
			this.$body.append($notificationsContainer);
		}

		return $notificationsContainer;
	}

	private showNext (): void {
		forEach(this.notificationsQueue._queue, (noty) => {
			invoke(noty, 'show');
		});
	}

	private startClosingTimer (noty: INoty, options?: ICommonNotyOptions): void {
		// Without zonejs
		noty.timer = window.setTimeout(noty.close, options?.timeout || this.NOTIFICATION_TIMEOUT);
	}

	private endClosingTimer (noty: INoty): void {
		if (noty.timer) {
			clearTimeout(noty.timer);
			noty.timer = null;
		}
	}

	protected restartClosingTimer (noty: INoty) {
		this.endClosingTimer(noty);
		this.startClosingTimer(noty);
	}

	private onAnimationEnd (callback: () => void): () => void {
		return () => {
			// Without zonejs
			setTimeout(callback, commonConstants.animationDuration);
		};
	}

	private animateAll (options: ICommonNotyOptions): void {
		const $notificationsContainer = this.getContainer(options);

		$notificationsContainer.children().each((elementIndex, element) => {
			const $element = $(element);
			const bottom = reduce(this.shownCache, (result, cacheItem, cacheIndex) => {
				if (cacheIndex > elementIndex) {
					return result + cacheItem.height;
				} else {
					return result;
				}
			}, 0);

			$element.css({
				bottom,
			});
		});
	}

	private showNotification (noty: INoty, options: ICommonNotyOptions): INoty {
		let $notificationsContainer: JQuery<HTMLElement>;

		if (!noty.shown) {
			if (!noty.queued) {
				this.showNext();
				this.notificationsQueue.add(noty);
			}

			$notificationsContainer = this.getContainer(options);

			if ($notificationsContainer.children().length < this.getMaxVisible()) {
				$notificationsContainer.append(this.createNotificationLayout(noty, options));

				this.notificationsQueue.remove(noty);
				noty.truncate();
				this.shownCache.push({
					noty,
					height: noty.$element.outerHeight(true)
				});

				this.animateAll(options);

				noty.shown = true;

				noty.$element
					.addClass(options.animation.open)
					.on('mouseenter', () => {
						this.endClosingTimer(noty);
					})
					.on('mouseleave', () => {
						this.startClosingTimer(noty, options);
					})
					// in IE animationend does not fire sometimes
					// emulated animationend from animationstart to ANIMATE_DURATION
					.on(this.animationStartEvents, this.onAnimationEnd(() => {
						this.startClosingTimer(noty, options);
						noty.$element.removeClass(options.animation.open);
						noty.$element.off(this.animationStartEvents);
					}));
			}
		}

		return noty;
	}

	public closeNotification (noty: INoty, options: ICommonNotyOptions): INoty {
		if (noty.shown) {
			this.endClosingTimer(noty);
			noty.isClosing = true;

			noty.$element
				.removeClass(options.animation.open)
				.addClass(options.animation.close)
				// in IE animationend does not fire sometimes
				// emulated animationend from animationstart to ANIMATE_DURATION
				.on(this.animationStartEvents, this.onAnimationEnd(() => {
					noty.shown = false;
					noty.$element.remove();
					remove(this.shownCache, (item) => {
						return item.noty.id === noty.id && item.noty.isClosing;
					});

					if (this.notificationsQueue._queue.length === 0) {
						this.animateAll(options);
					} else {
						this.showNext();
					}
				}));

			if (options?.type === 'promotion') {
				this.commonEventsService.emit({
					name: PROMOTION_BANNER_EVENT.CLOSE_BANNER,
					payload: options.notificationId,
				});
			}
		}

		return noty;
	}

	protected getDefaultOptions(closeButtonTitle: string): ICommonNotyOptions {
			return {
				text: '',
				type: 'success',
				layout: 'bottomLeft',
				icon: '',
				timeout: this.NOTIFICATION_TIMEOUT,
				animation: {
					open: 'slideInUp',
					close: 'slideOutLeftFade',
				},
				buttons: [
					{
						title: closeButtonTitle,
						className: 'b-noty_button_close',
						callback: (button) => {
							button.close();
						},
					},
				],
		};
	}

	public createNotification (options: ICommonNotyOptions): void {
		const defaultOptions = this.getDefaultOptions(this.commonLocaleService.instant('common.dismiss'));

		options = mergeWith({}, defaultOptions, options, (objValue, srcValue) => {
			if (isArray(objValue) && isArray(srcValue)) {
				return objValue.concat(srcValue);
			}
		});

		if (options.buttons) {
			options.buttons = reverse(options.buttons);  // correct buttons order with float: right
		}

		const noty: INoty = {
			id: options.notificationId,
			shown: false,
			queued: false,
			timer: null,
			isClosing: false,
			$element: $(`<div id='${options.notificationId}'></div>`),
			$body: $('<div class="b-noty_body"></div>'),
			$buttons: $('<div class="b-noty_buttons"></div>'),
			show: () => {
				return this.showNotification(noty, options);
			},
			close: () => {
				return this.closeNotification(noty, options);
			},
			truncate: () => {
				if (options?.type !== 'promotion') {
					// tslint:disable-next-line:no-unused-expression
					new Dotdotdot(
						document.querySelector('.b-noty-text-body--ellipsis'),
						{
							height: 160, // 10 text lines
						},
					);
				}
			},
		};

		noty.show();
	}

	private getMessage (title: string, body: string | {}): string {
		const sanitizedTitle = this.domSanitizer.sanitize(SecurityContext.HTML, title) || '';

		// CASEM-51657 span Need for IE 11
		let bodyContent: string;
		let bodyEl: string;

		if (isObject(body) && has(body, 'template')) {
			// now use template as string instead of templateUrl
			// this using in administration
			bodyContent = this.domSanitizer.sanitize(SecurityContext.HTML, get(body, 'template')) || '';
			bodyEl = `<div class='b-noty-text-body'>${bodyContent}</div>`;
		} else {
			bodyContent = this.domSanitizer.sanitize(SecurityContext.HTML, body as string) || '';
			bodyEl = `<div class='b-noty-text-body b-noty-text-body--ellipsis'><span>${bodyContent}</span></div>`;
		}

		const template = `
			<div class='b-noty-text-container'>
				<div class='b-noty-text-icon'></div>
				<div class='b-noty-text-info'>
						<div class='b-noty-text-title g-textoverflow' title='${sanitizedTitle}'>${sanitizedTitle}</div>
						${bodyEl}
				</div>
			</div>
		`;

		return template;
	}

	private initPromotionalEvent(noty: INoty, buttonInfo: ICommonNotyOptionsButton): void {
		const promotionalEventCreationRequest = {
			NotificationId: noty.id as UUID,
			ButtonInfo: {
				Label: buttonInfo.title,
				Url: buttonInfo.href,
			}
		};
		this.commonEventsService.emit({
			name: PROMOTION_BANNER_EVENT.INIT_PROMOTIONAL_EVENT,
			payload: promotionalEventCreationRequest,
		});
	}
}
